// src/components/CookieConsentBanner.jsx

import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CONSENT_KEY,
  applyConsent,
  getStoredConsent,
  setupGoogleConsentDefaults,
  trackPageView,
} from "../utils/tracking";

const defaultPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const CookieConsentBanner = () => {
  const location = useLocation();

  const [showBanner, setShowBanner] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  useEffect(() => {
    setupGoogleConsentDefaults();

    const savedConsent = getStoredConsent();

    if (savedConsent) {
      setPreferences({
        necessary: true,
        analytics: !!savedConsent.analytics,
        marketing: !!savedConsent.marketing,
      });

      applyConsent(savedConsent);

      setTimeout(() => {
        trackPageView();
      }, 300);
    } else {
      setShowBanner(true);
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      trackPageView();
    }, 300);
  }, [location.pathname, location.search]);

  const saveConsent = (newConsent) => {
    const consentRecord = {
      necessary: true,
      analytics: !!newConsent.analytics,
      marketing: !!newConsent.marketing,
      version: "1.0",
      source: "3dspot_cookie_banner",
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentRecord));

    setPreferences({
      necessary: true,
      analytics: consentRecord.analytics,
      marketing: consentRecord.marketing,
    });

    applyConsent(consentRecord);

    setTimeout(() => {
      trackPageView();
    }, 300);

    setShowBanner(false);
    setShowManage(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const rejectAll = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  const openSettings = () => {
    const savedConsent = getStoredConsent();

    if (savedConsent) {
      setPreferences({
        necessary: true,
        analytics: !!savedConsent.analytics,
        marketing: !!savedConsent.marketing,
      });
    }

    setShowBanner(true);
    setShowManage(true);
  };

  return (
    <>
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-black text-white px-4 py-4 shadow-2xl">
          <div className="max-w-6xl mx-auto">
            {!showManage ? (
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="max-w-3xl">
                  <h2 className="text-lg font-bold mb-1">We use cookies</h2>

                  <p className="text-sm text-gray-200">
                    We use essential cookies to run our website. With your
                    permission, we also use analytics and advertising cookies,
                    including Google Analytics, Google Ads tags, and Meta Pixel,
                    to measure performance and improve our ads.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 min-w-fit">
                  <button
                    type="button"
                    onClick={rejectAll}
                    className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white font-semibold"
                  >
                    Reject
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowManage(true)}
                    className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white font-semibold"
                  >
                    Manage preferences
                  </button>

                  <button
                    type="button"
                    onClick={acceptAll}
                    className="px-4 py-2 rounded bg-white hover:bg-gray-100 text-black font-semibold"
                  >
                    Accept
                  </button>
                </div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl font-bold mb-3">
                  Cookie preferences
                </h2>

                <p className="text-sm text-gray-200 mb-4">
                  Choose which optional cookies we may use. Essential cookies
                  are always active because they are required for website
                  security and basic functionality.
                </p>

                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4 border border-gray-700 rounded p-3">
                    <div>
                      <h3 className="font-bold">Essential cookies</h3>
                      <p className="text-sm text-gray-300">
                        Required for website security, checkout, login, and
                        basic website functions. These cannot be disabled.
                      </p>
                    </div>

                    <input type="checkbox" checked disabled />
                  </div>

                  <div className="flex items-start justify-between gap-4 border border-gray-700 rounded p-3">
                    <div>
                      <h3 className="font-bold">Analytics cookies</h3>
                      <p className="text-sm text-gray-300">
                        Help us understand website traffic and performance using
                        tools such as Google Analytics.
                      </p>
                    </div>

                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) =>
                        setPreferences((prev) => ({
                          ...prev,
                          analytics: e.target.checked,
                        }))
                      }
                    />
                  </div>

                  <div className="flex items-start justify-between gap-4 border border-gray-700 rounded p-3">
                    <div>
                      <h3 className="font-bold">Marketing cookies</h3>
                      <p className="text-sm text-gray-300">
                        Help us measure ads and show relevant ads using tools
                        such as Meta Pixel, Google Ads tags, remarketing, and
                        custom audiences.
                      </p>
                    </div>

                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) =>
                        setPreferences((prev) => ({
                          ...prev,
                          marketing: e.target.checked,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 mt-5">
                  <button
                    type="button"
                    onClick={rejectAll}
                    className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white font-semibold"
                  >
                    Reject all
                  </button>

                  <button
                    type="button"
                    onClick={savePreferences}
                    className="px-4 py-2 rounded bg-white hover:bg-gray-100 text-black font-semibold"
                  >
                    Save preferences
                  </button>

                  <button
                    type="button"
                    onClick={acceptAll}
                    className="px-4 py-2 rounded bg-white hover:bg-gray-100 text-black font-semibold"
                  >
                    Accept all
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={openSettings}
        className="fixed bottom-4 left-4 z-[9998] bg-gray-900 text-white text-xs px-3 py-2 rounded-full shadow-lg"
      >
        Cookie settings
      </button>
    </>
  );
};

export default CookieConsentBanner;